import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import SprocketeerRegister from './components/SprocketeerRegister';
import HUGRegister from './components/HUGRegister';
import Dashboard from './components/Dashboard';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ManageAccount from './components/ManageAccount';
import Credits from './components/Credits';
import PortalConfiguration from './components/PortalConfiguration';
import Pricing from './components/Pricing';
import Support from './components/Support';
import SetupGuide from './components/SetupGuide';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import WhyUs from './components/WhyUs';
import UnsubscribeCredits from './components/UnsubscribeCredits';
import AdminDashboard from './components/AdminDashboard';
import Footer from './components/Footer';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log('Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-content">
            <h1>Something went wrong</h1>
            <p>We're sorry, but there was an error loading this page. Please try refreshing or contact support if the problem persists.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="btn btn-primary"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  console.log('App component rendered');
  
  useEffect(() => {
    // LinkedIn tracking code
    window._linkedin_partner_id = "7100572";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
    
    if (!window.lintrk) {
      window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
      window.lintrk.q=[];
    }
    
    const s = document.getElementsByTagName("script")[0];
    const b = document.createElement("script");
    b.type = "text/javascript";
    b.async = true;
    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    s.parentNode.insertBefore(b, s);
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <Navbar />
            
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/register/sprocketeer" element={<SprocketeerRegister />} />
                <Route path="/register/hug" element={<HUGRegister />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/pricing" element={<PublicOnlyRoute><Pricing /></PublicOnlyRoute>} />
                <Route path="/why-us" element={<WhyUs />} />
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/manage-account" element={<PrivateRoute><ManageAccount /></PrivateRoute>} />
                <Route path="/credits" element={<PrivateRoute><Credits /></PrivateRoute>} />
                <Route path="/configure-portal/:portalId" element={<PrivateRoute><PortalConfiguration /></PrivateRoute>} />
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/support" element={<Support />} />
                <Route path="/setup-guide" element={<SetupGuide />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/unsubscribe/:token" element={<UnsubscribeCredits />} />
              </Routes>
            </main>
            
            <Footer />
            
            {/* LinkedIn Tracking Pixel */}
            <div id="linkedin-tracking" style={{ display: 'none' }}>
              <noscript>
                <img 
                  height="1" 
                  width="1" 
                  style={{display: "none"}} 
                  alt="" 
                  src="https://px.ads.linkedin.com/collect/?pid=7100572&fmt=gif" 
                />
              </noscript>
            </div>
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Private route component
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin route component (only accessible by admin users)
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!user || !user.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public only route component (for pages like login that shouldn't be accessed when logged in)
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

export default App;
